import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

const faqs = [
  {
    id: 1,
    question: "How long does the trademark registration process take?",
    answer: "The entire trademark registration process typically takes 8-12 months from filing to approval. However, we begin monitoring your trademark immediately after filing, and you can start using the ™ symbol right away."
  },
  {
    id: 2,
    question: "What's the difference between ™ and ® symbols?",
    answer: "The ™ symbol can be used immediately when you claim rights to a trademark, even before registration. The ® symbol can only be used after your trademark is officially registered with the USPTO, providing you with federal legal protection."
  },
  {
    id: 3,
    question: "Can I trademark a name that's similar to an existing one?",
    answer: "It depends on several factors including industry, geographic location, and how similar the marks are. Our comprehensive trademark search will identify potential conflicts, and our experts will advise you on the likelihood of successful registration."
  },
  {
    id: 4,
    question: "What can I trademark?",
    answer: "You can trademark names, logos, slogans, sounds, colors, and even product shapes that identify and distinguish your goods or services. We'll help you determine the best strategy to protect your brand's unique elements."
  },
  {
    id: 5,
    question: "Do I need a lawyer to register a trademark?",
    answer: "While not legally required, having professional assistance significantly increases your chances of approval. Our team of trademark experts handles all the complexities, from comprehensive searches to responding to USPTO office actions."
  },
  {
    id: 6,
    question: "What happens if someone infringes on my trademark?",
    answer: "Once your trademark is registered, you have legal grounds to take action against infringers. This includes sending cease and desist letters, negotiating settlements, or pursuing litigation if necessary. We provide guidance on protecting your registered trademark."
  }
];

export const FAQSection = () => {
  const [openId, setOpenId] = useState(null);

  const toggleFAQ = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <HelpCircle size={18} />
              <span>FAQ</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about trademark registration
            </p>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div
                key={faq.id}
                className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                      openId === faq.id ? 'rotate-180' : ''
                    }`}
                    size={24}
                  />
                </button>
                
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openId === faq.id ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-5 pt-2">
                    <p className="text-base text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 text-center p-8 bg-gradient-to-r from-blue-50 to-orange-50 rounded-2xl border-2 border-primary/10">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Still have questions?
            </h3>
            <p className="text-base text-gray-600 mb-4">
              Our team is here to help you with any concerns about trademark registration
            </p>
            <a
              href="/contact"
              className="inline-flex items-center text-primary font-semibold hover:underline"
            >
              Contact Us
              <ChevronDown className="ml-1 rotate-[-90deg]" size={18} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
